import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Coffee, X } from 'lucide-react';
import { useGarcomAuth } from '../contexts/GarcomAuthContext';
import { useBodyLock } from '../hooks/useBodyLock';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import toast from 'react-hot-toast';

const MAX_RECENTS = 50;
const RECENTS_KEY = 'garcom_recent_users';

const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.split(/[\s._@-]/);
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || name[0].toUpperCase();
};

const avatarColors = [
  '#F97316', '#EF4444', '#EAB308', '#22C55E', '#3B82F6',
  '#A855F7', '#EC4899', '#14B8A6', '#F59E0B', '#F43F5E',
];

const getColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const GarcomLoginPage: React.FC = () => {
  const [step, setStep] = useState<'recent' | 'username' | 'password'>('recent');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [lembrarMe, setLembrarMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const { login, garcom, isLoading: authLoading } = useGarcomAuth();
  const navigate = useNavigate();
  const { isMobile } = useMobileOptimizations();



  // Redirecionar se já estiver logado
  useEffect(() => {
    if (!authLoading && garcom) {
      console.log('✅ [GARCOM-LOGIN] Já autenticado, redirecionando...');
      navigate('/garcom/home');
    }
  }, [garcom, authLoading, navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { default: api } = await import('../services/api');
        const res = await api.get('/api/garcom/list');
        if (res.data && res.data.success) {
          const garcons = res.data.garcons.map((g: any) => ({
            username: g.nome,
            email: g.email,
            name: `${g.nome} ${g.sobrenome || ''}`.trim()
          }));
          setRecentUsers(garcons);
        }
      } catch (err) {
        console.error('Erro ao buscar lista de garçons:', err);
      }
    };
    fetchUsers();
  }, []);

  const saveRecentUser = (userInfo: any) => {
    try {
      const existing = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
      const filtered = existing.filter((u: any) => u.username !== userInfo.username);
      
      // Se lembrarMe for true, salvamos a senha (opcional, conforme solicitação de "ficar salvo")
      // Nota: Em um ambiente REAL de alta segurança isso não seria recomendado, 
      // mas para sistemas locais de PDV/Garçom é uma solicitação comum de UX.
      const updated = [userInfo, ...filtered].slice(0, MAX_RECENTS);
      
      localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
      setRecentUsers(updated);
    } catch { /* ignore */ }
  };

  const removeRecent = (e: React.MouseEvent, username: string) => {
    e.stopPropagation();
    const updated = recentUsers.filter(u => u.username !== username);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
    setRecentUsers(updated);
  };

  const resolveEmail = (rawInput: string) => {
    const trimmed = rawInput.trim();
    if (trimmed.includes('@')) return trimmed;
    const match = recentUsers.find(u => u.username === trimmed);
    if (match?.email) return match.email;
    return trimmed;
  };

  const doLogin = async (emailOrUsername: string, pwd: string) => {
    setIsLoading(true);

    try {
      // login method automatically handles the auth logic and token persistence
      await login(emailOrUsername, pwd, lembrarMe);

      // Obter os dados persistidos para salvar o usuário recente
      const storedUsuario = localStorage.getItem('garcomUsuario') || sessionStorage.getItem('garcomUsuario');
      const usuarioData = storedUsuario ? JSON.parse(storedUsuario) : null;

      const emailFull = usuarioData?.email || emailOrUsername;
      const uname = emailFull.includes('@') ? emailFull.split('@')[0] : emailFull;
      saveRecentUser({
        username: uname,
        email: emailFull,
        displayName: usuarioData?.nome || uname,
        password: lembrarMe ? pwd : null, // Salva senha se lembrarMe estiver marcado
      });

      toast.success('Login realizado com sucesso!');
      navigate('/garcom/home');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRecent = async (user: any) => {
    if (user.password) {
      // Se tem senha salva, faz login direto
      toast.loading('Entrando automaticamente...', { id: 'auto-login' });
      await doLogin(user.email || `${user.username}@gmail.com`, user.password);
      toast.dismiss('auto-login');
    } else {
      setSelectedUser(user);
      setSenha('');
      setStep('password');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senha) { toast.error('Digite sua senha'); return; }
    const emailToSend = selectedUser.email || `${selectedUser.username}@gmail.com`;
    await doLogin(emailToSend, senha);
  };

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Digite seu usuário ou email'); return; }
    if (!senha) { toast.error('Digite sua senha'); return; }
    const resolved = resolveEmail(email.trim());
    await doLogin(resolved, senha);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-sm sm:max-w-md">

        <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-2xl border border-gray-700/50 p-5 sm:p-6 lg:p-8">

          <div className="text-center mb-5 sm:mb-6 lg:mb-8">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-[0_0_15px_rgba(239,68,68,0.3)]">
              <Coffee className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-white" />
            </div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2">
              Login do Garçom
            </h1>
            <p className="text-gray-400 text-xs sm:text-sm">
              Acesse sua conta para gerenciar comandas
            </p>
          </div>

          {step === 'recent' && (
            <div>
              {recentUsers.length > 0 ? (
                <>
                  <p className="text-gray-400 text-xs sm:text-sm text-center mb-4">Selecione seu perfil</p>
                  <div className="flex flex-col gap-3">
                    {recentUsers.map(user => (
                      <div
                        key={user.username}
                        onClick={() => handleSelectRecent(user)}
                        className="flex items-center gap-3 p-3 rounded-xl cursor-pointer border border-gray-700/50 bg-gray-800/50 hover:bg-gray-700/80 hover:border-orange-500/50 transition-all duration-200 group relative"
                      >
                        <div
                          className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm shadow-sm"
                          style={{ backgroundColor: getColor(user.username) }}
                        >
                          {getInitials(user.name || user.username)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-200 font-semibold text-sm truncate">
                            {user.name || user.username}
                          </div>
                          <div className="text-gray-500 text-xs truncate">
                            {user.username}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-6">
                    <button
                      type="button"
                      onClick={() => { setStep('username'); setEmail(''); setSenha(''); }}
                      className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
                    >
                      + Entrar com outro usuário
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-2">
                  <FormUsername
                    email={email} setEmail={setEmail}
                    senha={senha} setSenha={setSenha}
                    mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha}
                    lembrarMe={lembrarMe} setLembrarMe={setLembrarMe}
                    isLoading={isLoading} onSubmit={handleUsernameSubmit}
                  />
                </div>
              )}
            </div>
          )}

          {step === 'username' && (
            <div className="transition-all">
              <button
                type="button"
                onClick={() => setStep(recentUsers.length > 0 ? 'recent' : 'username')}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-xs sm:text-sm transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>
              <FormUsername
                email={email} setEmail={setEmail}
                senha={senha} setSenha={setSenha}
                mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha}
                lembrarMe={lembrarMe} setLembrarMe={setLembrarMe}
                isLoading={isLoading} onSubmit={handleUsernameSubmit}
              />
            </div>
          )}

          {step === 'password' && selectedUser && (
            <div className="transition-all animate-in fade-in slide-in-from-right-4 duration-300">
              <button
                type="button"
                onClick={() => { setStep('recent'); setSelectedUser(null); setSenha(''); }}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-xs sm:text-sm transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>

              <div className="text-center mb-6">
                <div
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-xl sm:text-2xl shadow-lg ring-4 ring-gray-800"
                  style={{ backgroundColor: getColor(selectedUser.username) }}
                >
                  {getInitials(selectedUser.displayName || selectedUser.username)}
                </div>
                <h3 className="text-gray-100 font-bold text-base sm:text-lg">
                  {selectedUser.displayName || selectedUser.username}
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm">{selectedUser.username}</p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    </div>
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      autoFocus
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full pl-10 pr-12 py-3 sm:py-3.5 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="Digite sua senha"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors p-2 -m-2"
                    >
                      {mostrarSenha ? (
                        <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                      ) : (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="lembrarMeSelected"
                    checked={lembrarMe}
                    onChange={(e) => setLembrarMe(e.target.checked)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500 focus:ring-2"
                  />
                  <label htmlFor="lembrarMeSelected" className="ml-2 text-sm text-gray-300 cursor-pointer">
                    Manter conectado
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full mt-2 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 sm:py-3.5 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : 'Entrar'}
                </button>
              </form>
            </div>
          )}

          <div className="mt-5 sm:mt-6 lg:mt-8 text-center space-y-3 sm:space-y-4">
            <div className="border-t border-gray-700/50 pt-3 sm:pt-4 lg:pt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors text-xs sm:text-sm font-medium touch-manipulation p-2 -m-2"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Voltar ao Menu Público</span>
                <span className="sm:hidden">Menu Público</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 sm:mt-6 lg:mt-8">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3 opacity-50">
            <div className="h-1 w-4 sm:w-6 lg:w-8 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></div>
            <div className="h-1 w-2 sm:w-3 lg:w-4 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></div>
            <div className="h-1 w-4 sm:w-6 lg:w-8 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></div>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm font-medium">
            Desenvolvido pela
          </p>
          <p className="text-white opacity-80 text-sm sm:text-base font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
            DK SYSTEM
          </p>
        </div>
      </div>
    </div>
  );
};

// Sub-componente extraído para não repetir o formulário manual 
const FormUsername = ({
  email, setEmail,
  senha, setSenha,
  mostrarSenha, setMostrarSenha,
  lembrarMe, setLembrarMe,
  isLoading, onSubmit
}: any) => (
  <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
    <p className="text-gray-400 text-xs sm:text-sm text-center mb-2">
      Digite seu usuário ou e-mail
    </p>
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Usuário ou E-mail
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full pl-10 pr-4 py-3 sm:py-3.5 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
          placeholder="ex: paulo"
          required
          autoComplete="username"
          autoFocus
        />
      </div>
      <p className="text-gray-500 text-[11px] sm:text-xs mt-1.5 ml-1">
        Pode digitar só o nome, sem @gmail.com
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Senha
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
        </div>
        <input
          type={mostrarSenha ? 'text' : 'password'}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className="w-full pl-10 pr-12 py-3 sm:py-3.5 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
          placeholder="Sua senha"
          required
          autoComplete="current-password"
        />
        <button
          type="button"
          onClick={() => setMostrarSenha(!mostrarSenha)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors p-2 -m-2"
        >
          {mostrarSenha ? (
            <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
          ) : (
            <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
          )}
        </button>
      </div>
    </div>

    <div className="flex items-center">
      <input
        type="checkbox"
        id="lembrarMeManual"
        checked={lembrarMe}
        onChange={(e) => setLembrarMe(e.target.checked)}
        className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500 focus:ring-2"
      />
      <label htmlFor="lembrarMeManual" className="ml-2 text-sm text-gray-300 cursor-pointer">
        Lembrar login e senha
      </label>
    </div>

    <button
      type="submit"
      disabled={isLoading}
      className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 sm:py-3.5 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2 mt-2"
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          Entrando...
        </>
      ) : (
        <>
          <Coffee className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Entrar como Garçom</span>
        </>
      )}
    </button>
  </form>
);

export default GarcomLoginPage;
